import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectBillingAddress, selectCustomerAddress, updateExternalDeliveryId } from "redux/actions";
import "./address-info.scss";
import { getLocalStorageValue } from 'config/helper';
import useShippingLabel from 'hooks/useShippingLabel';
import { useGetCustomerAddressList } from 'hooks/useGetCustomerAddressList';
import { AddressTypes } from 'config/constant';

const AddressInfo = ({ handleAddNewAddress, isBillingAddress }) => {
  const dispatch = useDispatch();

  const { shippingLabelLower, shippingLabelTitle } = useShippingLabel(isBillingAddress)
  const { customerAddressList, customerAddressListLoading } = useGetCustomerAddressList();
  const { deliveryLockerType } = useSelector(({ checkout }) => checkout);

  const {
    checkoutStoreType,
    customerAddress: customerAddressId,
    isFollowedEbtPayment,
    savePlaceOrder,
    billingAddress,
  } = useSelector(({checkout}) => checkout)
  const { storeListData } = useSelector(({ store }) => store);
  const selectedStore = storeListData?.find(store => store.ClientStoreId === getLocalStorageValue("selected-store-id"));

  const addressStatus = isBillingAddress ?  AddressTypes.billing : AddressTypes.shipping
  const setAddressFunc = isBillingAddress ? selectBillingAddress : selectCustomerAddress
  const selectedAddressId = isBillingAddress ? billingAddress : customerAddressId

  const addressList = useMemo(() => {
    return customerAddressList?.filter(a => a.status === addressStatus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAddressList, customerAddressListLoading])

  const {
    AddressLine1 = "",
    AddressLine2 = "",
    City = "",
    ZipCode = "",
    StateName = "",
  } = selectedStore || {};
  
   const modifyOrderId = useSelector(({ checkout }) => checkout.modifyOrderId);

  useEffect(() => {
    if (isFollowedEbtPayment && !isBillingAddress) return 
    
    if (modifyOrderId || !addressList?.length) return;
    
    const defaultSelectedAddress = addressList?.find(a => a.status === addressStatus && a.is_primary === "1");
    if (defaultSelectedAddress !== undefined) {
      dispatch(setAddressFunc(defaultSelectedAddress.address_id));
    }
    else {
      dispatch(setAddressFunc(""));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, addressList, isFollowedEbtPayment]);

  const handleAddressChange = (e) => {
    const { value } = e.target;
    dispatch(setAddressFunc(value));
    if (!isBillingAddress) {
      dispatch(updateExternalDeliveryId(""));
    }
  };

  if (!isBillingAddress) {
    if (isFollowedEbtPayment && savePlaceOrder?.shipping_method === "delivery") {
      return (
        <Address
          title={`Your ${shippingLabelTitle} location`}
          AddressLine1={savePlaceOrder?.shipping_address_1}
          AddressLine2={savePlaceOrder?.shipping_address_2}
          City={savePlaceOrder?.shipping_city}
          StateName={savePlaceOrder?.shipping_state}
          ZipCode={savePlaceOrder?.shipping_zipcode}
        />
      )
    }
  
    if (checkoutStoreType === "store_pickup") {
      let lockerText = +deliveryLockerType === 1 ? " (Pickup from locker)" : ""
      return (
        <Address
          title={`Your pickup location${lockerText}`}
          AddressLine1={AddressLine1}
          AddressLine2={AddressLine2}
          City={City}
          StateName={StateName}
          ZipCode={ZipCode}
        />
      )
    }
  }


  return (
    <Layout>
          <div>
            <h1>{`Choose your ${shippingLabelLower} address`}</h1>
            {<button
              onClick={handleAddNewAddress}
              className="primary-outlined-button"
            >
              Add new address
            </button>}
          </div>

          {addressList?.map(list => {
              const {
                first_name,
                last_name,
                address_1,
                address_2,
                city,
                state,
                zip_code,
                address_id,
              } = list;
              return (
                <div className="radio-alignment" key={address_id}>
                  <div>
                    <input
                      type="radio"
                      id={`AddressCard${address_id}`}
                      name={isBillingAddress ? "billing-address" : "shipping-address"}
                      value={address_id}
                      onChange={handleAddressChange}
                      checked={selectedAddressId === address_id}
                    />
                    <label htmlFor={`AddressCard${address_id}`}>&nbsp;</label>
                    <p>
                      {first_name} {last_name} <br></br>
                      {address_1} {address_2 ? address_2 : ""},<br></br>
                      {city}, {state}, {zip_code}
                    </p>
                  </div>
                </div>
              );
            })}
          {!selectedAddressId && (
            <span className="contact-helper-text" >
              *Please choose address for {shippingLabelLower}
            </span>
          )}
    </Layout>
  );
};

function Layout({children}) {
  return (
    <>
      <div className="address-info-wrapper">
        <div className="address-info-section">{children}</div>
      </div>
    </>
  )
}

function Address({title,AddressLine1, AddressLine2, City, StateName, ZipCode}) {
  return (
    <Layout>
        <div>
          <h1>{title}</h1>
        </div>

        <div className="radio-alignment">
          <div>
            <p>
              {AddressLine1} {AddressLine2 ? AddressLine2 : ""},<br></br>
              {City}, {StateName}, {ZipCode}
            </p>
          </div>
        </div>
    </Layout>
  )
}

export default AddressInfo;